/**
 * Console icons — a small set of consistent, stroke-based line icons used by the
 * sidebar (so the collapsed icon-rail is legible) and the top bar.
 *
 * Every icon is a 24×24 inline SVG drawn with `currentColor` at stroke-width
 * 1.75, so it inherits the surrounding text colour (ivory / sage-tint on the
 * emerald sidebar) and stays crisp at the rail size (20px). Icons are
 * decorative (`aria-hidden`) — the accessible label is always the adjacent
 * text or an `aria-label` on the control.
 */
import type { ReactElement } from "react";
import type { ModuleKey } from "./modules.js";

/** Shared props for a line icon. */
interface IconProps {
  readonly size?: number;
}

/** Wrap a set of SVG children in a consistently-configured <svg>. */
function svg(size: number, children: ReactElement): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Icon for each console module, keyed by {@link ModuleKey}. */
export function ModuleIcon({
  moduleKey,
  size = 20,
}: {
  readonly moduleKey: ModuleKey;
  readonly size?: number;
}): ReactElement {
  switch (moduleKey) {
    case "dashboard":
      return svg(
        size,
        <>
          <rect x="3" y="3" width="7" height="9" rx="1.5" />
          <rect x="14" y="3" width="7" height="5" rx="1.5" />
          <rect x="14" y="12" width="7" height="9" rx="1.5" />
          <rect x="3" y="16" width="7" height="5" rx="1.5" />
        </>,
      );
    case "properties":
      return svg(
        size,
        <>
          <path d="M3 10.5 12 4l9 6.5" />
          <path d="M5 9.5V20h14V9.5" />
          <path d="M10 20v-5h4v5" />
        </>,
      );
    case "bookings":
      return svg(
        size,
        <>
          <rect x="3.5" y="4" width="17" height="17" rx="2" />
          <path d="M3.5 9h17" />
          <path d="M7 13h6M7 17h9" />
        </>,
      );
    case "booking":
      return svg(
        size,
        <>
          <rect x="3.5" y="5" width="17" height="16" rx="2" />
          <path d="M3.5 9h17M8 3v4M16 3v4" />
          <path d="M12 13v4M10 15h4" />
        </>,
      );
    case "channels":
      return svg(
        size,
        <>
          <path d="M4 7h11a4 4 0 0 1 0 8H9" />
          <path d="m7 12-3 3 3 3" />
          <circle cx="19" cy="7" r="1.4" />
        </>,
      );
    case "reconciliation":
      return svg(
        size,
        <>
          <path d="M12 4v16" />
          <path d="M5 7h5a3 3 0 0 1 0 6H5l3-3M19 17h-5a3 3 0 0 1 0-6h5l-3 3" />
        </>,
      );
    case "revenue":
      return svg(
        size,
        <>
          <path d="M4 19V5M4 19h16" />
          <path d="m7 15 3.5-4 3 2.5L20 8" />
        </>,
      );
    case "crm":
      return svg(
        size,
        <>
          <circle cx="9" cy="8" r="3.2" />
          <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
          <path d="M16.5 6.2a3 3 0 0 1 0 5.6M17.5 20a5.5 5.5 0 0 0-3-4.9" />
        </>,
      );
    case "automation":
      return svg(
        size,
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
        </>,
      );
    case "marketing":
      return svg(
        size,
        <>
          <path d="M3 10v4a1 1 0 0 0 1 1h3l7 4V5L7 9H4a1 1 0 0 0-1 1Z" />
          <path d="M18 9a4 4 0 0 1 0 6" />
        </>,
      );
    case "operations":
      return svg(
        size,
        <>
          <path d="M4 7h10M4 12h16M4 17h7" />
          <circle cx="18" cy="7" r="1.6" />
          <circle cx="16" cy="17" r="1.6" />
        </>,
      );
    case "agents":
      return svg(
        size,
        <>
          <rect x="5" y="7" width="14" height="11" rx="3" />
          <path d="M12 3v4M9 12h.01M15 12h.01" />
          <path d="M3.5 12H5M19 12h1.5" />
        </>,
      );
    case "analytics":
      return svg(
        size,
        <>
          <path d="M4 20V4" />
          <rect x="7" y="12" width="3" height="8" rx="1" />
          <rect x="12.5" y="8" width="3" height="12" rx="1" />
          <rect x="18" y="5" width="3" height="15" rx="1" />
        </>,
      );
    case "accounts":
      return svg(
        size,
        <>
          {/* A ledger book with a rule line — the "books". */}
          <path d="M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2Z" />
          <path d="M9 4v16" />
          <path d="M12 9h3M12 12h3" />
        </>,
      );
    case "admin":
      return svg(
        size,
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2.5v2.2M12 19.3v2.2M4.2 7l1.9 1.1M17.9 15.9l1.9 1.1M4.2 17l1.9-1.1M17.9 8.1l1.9-1.1" />
        </>,
      );
    default:
      return svg(size, <circle cx="12" cy="12" r="8" />);
  }
}

/** A bell (notifications), with an optional filled dot for unread. */
export function BellIcon({ size = 20 }: IconProps): ReactElement {
  return svg(
    size,
    <>
      <path d="M6 9a6 6 0 0 1 12 0c0 5 1.5 6.5 2 7H4c.5-.5 2-2 2-7Z" />
      <path d="M9.5 20a2.5 2.5 0 0 0 5 0" />
    </>,
  );
}

/** A hamburger / menu-collapse glyph for the sidebar toggle. */
export function MenuIcon({ size = 20 }: IconProps): ReactElement {
  return svg(
    size,
    <>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </>,
  );
}

/** A chevron used to indicate a dropdown trigger. */
export function ChevronDownIcon({ size = 16 }: IconProps): ReactElement {
  return svg(size, <path d="m6 9 6 6 6-6" />);
}

/** A sign-out glyph for the profile menu. */
export function SignOutIcon({ size = 18 }: IconProps): ReactElement {
  return svg(
    size,
    <>
      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
      <path d="M10 12H3m0 0 3-3m-3 3 3 3" />
    </>,
  );
}
